// Phone ⇄ runner messages (C-link): the direct route while it answers pings, else the DC relay; `?link=relay` forces the relay.

import store from '~/store'
import { ABLY_CLIENT_ID, DC_MESSAGES, DEVICE_TYPES, IS_DEV, QUERY } from '~/lib/constants'
import socket from '~/lib/socket'
import { createPeer } from './p2p'
import {
	P2P_CONNECT_MS,
	P2P_PING_MS,
	PING_MS,
	addClockSample,
	clockSample,
	linkRole,
	linkRoute,
	p2pLive,
	p2pRetryDelay,
} from './route'

const logger = console.scope('desktopLink')
const FORCE_RELAY = QUERY.link === 'relay'

const handlers = new Map()
const listeners = new Set()
// name → { peer, open, p2pAt, startedAt, rideSeq }: the remote's one peer (the runner), or the runner's one per remote. `rideSeq` = the highest `ride` seq seen on it; an older frame is stale.
const peers = new Map()
const emptyClocks = () => ({ relay: { samples: [] }, p2p: { samples: [] } })
let clocks = emptyClocks()
let retry = { failures: 0, nextAt: 0 }
let seq = 0
let lastPongAt = null
let lastRelayPingAt = 0
let pingTimer = null
let inputs = {}
// `offset` (runner − local clock) is null until the first pong: a reading dated on the runner cannot be placed before it.
let snapshot = { role: null, route: 'down', runner: null, rtt: null, offset: null, lastMessageAt: null }

const set = patch => {
	snapshot = { ...snapshot, ...patch }
	listeners.forEach(fn => fn(snapshot))
}

const remoteNames = () =>
	(store.getState().app.dc_connections ?? []).filter(c => c.device_type === DEVICE_TYPES.REMOTE).map(c => c.name)

const directTo = (name, now = Date.now()) => p2pLive({ p2pAt: peers.get(name)?.p2pAt, now, forceRelay: FORCE_RELAY })

const postVia = (via, type, payload, to, label = 'ctl') => {
	const msg = { type, seq: ++seq, t: Date.now(), payload }
	if (via === 'p2p' && peers.get(to)?.peer.send(label, msg)) return
	socket.sendDC(DC_MESSAGES.LINK, { to, msg })
}

const post = (type, payload, to, label) => postVia(directTo(to) ? 'p2p' : 'relay', type, payload, to, label)

/**
 * Sends `type` over the link. A remote's goes to the runner; the runner's to `to`, or to every remote when omitted. `ride` = a gesture `write` frame: unordered, never retransmitted, stale ones dropped. False (and dropped) while down.
 */
export const send = (type, payload, { to, ride } = {}) => {
	const { role, route, runner } = snapshot
	if (route === 'down') return false
	const label = ride ? 'ride' : 'ctl'
	if (role === 'remote') post(type, payload, runner, label)
	else for (const name of to ? [to] : remoteNames()) post(type, payload, name, label)
	return true
}

/** `fn(payload, { from, seq, t })` for each inbound `type`; returns the unsubscribe. */
export const on = (type, fn) => {
	if (!handlers.has(type)) handlers.set(type, new Set())
	handlers.get(type).add(fn)
	return () => handlers.get(type).delete(fn)
}

/** `fn(snapshot)` on every role, route or clock change; returns the unsubscribe. */
export const onChange = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

export const getSnapshot = () => snapshot

/** A local wall-clock time on the runner's clock. */
export const toRunnerTime = t => t + (snapshot.offset ?? 0)

/** A runner wall-clock time on the local clock; null while the offset is unknown. */
export const fromRunnerTime = t => (snapshot.offset == null ? null : t - snapshot.offset)

const closePeer = name => peers.get(name)?.peer.close()

const openPeer = (name, initiator) => {
	const entry = { open: false, p2pAt: null, startedAt: Date.now() }
	entry.peer = createPeer({
		initiator,
		logger,
		onSignal: (kind, data) =>
			socket.sendToSession(`link_${kind}`, {
				from: ABLY_CLIENT_ID,
				to: name,
				[kind === 'ice' ? 'candidate' : 'sdp']: data,
			}),
		onMessage: (msg, label) => receive({ from: name, msg }, label === 'ride' ? 'ride' : 'p2p'),
		onOpen: () => {
			entry.open = true
			retry.failures = 0
			logger.info('p2p open', { peer: name })
			if (snapshot.role === 'remote') postVia('p2p', 'ping', undefined, name)
		},
		onClose: () => {
			if (peers.get(name) === entry) peers.delete(name)
			if (!entry.open) retry.failures++
			retry.nextAt = Date.now() + p2pRetryDelay(Math.max(retry.failures, 1))
			logger.info('p2p closed', { peer: name, opened: entry.open, failures: retry.failures })
			// Closes also come from inside evaluate (role change, remote gone).
			queueMicrotask(evaluate)
		},
	})
	peers.set(name, entry)
	return entry
}

/** The remote keeps one direct attempt alive toward the runner, backing off on failures. */
const maintainP2P = now => {
	const { role, runner, route } = snapshot
	if (FORCE_RELAY || role !== 'remote' || route === 'down') return
	const entry = peers.get(runner)
	if (entry && !entry.open && now - entry.startedAt > P2P_CONNECT_MS) return closePeer(runner)
	if (!entry && now >= retry.nextAt) openPeer(runner, true)
}

const evaluate = () => {
	const s = store.getState().app
	const role = linkRole({ me: ABLY_CLIENT_ID, runner: s.runner_device, connections: s.dc_connections })
	if (role !== snapshot.role || s.runner_device !== snapshot.runner) {
		// A new peer on the other end: its routes, clock and liveness start over.
		;[...peers.keys()].forEach(closePeer)
		clocks = emptyClocks()
		retry = { failures: 0, nextAt: 0 }
		lastPongAt = null
		lastRelayPingAt = 0
		clearInterval(pingTimer)
		pingTimer = role === 'remote' ? setInterval(tick, P2P_PING_MS) : null
		// Down until the new runner answers: a listener must not see the old route under the new runner.
		set({ role, runner: s.runner_device, route: 'down', rtt: null, offset: null, lastMessageAt: null })
		if (role === 'remote') return tick()
	}
	if (role === 'runner') {
		const remotes = remoteNames()
		;[...peers.keys()].filter(name => !remotes.includes(name)).forEach(closePeer)
	}
	const route = linkRoute({
		role,
		dcOpen: s.dc_state === 'open',
		runner: s.runner_device,
		connections: s.dc_connections,
		lastPongAt,
		p2pAt: peers.get(s.runner_device)?.p2pAt,
		now: Date.now(),
		forceRelay: FORCE_RELAY,
	})
	const clock = clocks[route === 'p2p' ? 'p2p' : 'relay']
	const next = { route, rtt: clock.rtt != null ? Math.round(clock.rtt) : null, offset: clock.offset ?? null }
	if (next.route !== snapshot.route) logger.info('route', { role, route })
	if (next.route !== snapshot.route || next.rtt !== snapshot.rtt || next.offset !== snapshot.offset) set(next)
}

function tick() {
	const now = Date.now()
	const { runner } = snapshot
	if (now - lastRelayPingAt >= PING_MS && store.getState().app.dc_state === 'open') {
		lastRelayPingAt = now
		postVia('relay', 'ping', undefined, runner)
	}
	if (peers.get(runner)?.open) postVia('p2p', 'ping', undefined, runner)
	evaluate()
	maintainP2P(now)
}

/** One inbound frame: DC `link { from, msg }` (`via` 'relay'), or a direct channel's ('p2p' = `ctl`, 'ride'). */
export const receive = ({ from, msg } = {}, via = 'relay') => {
	if (!msg?.type) return
	const { role, runner } = snapshot
	if (role === 'runner' ? !remoteNames().includes(from) : role !== 'remote' || from !== runner) return
	const now = Date.now()
	const route = via === 'relay' ? 'relay' : 'p2p'
	if (route === 'p2p' && peers.has(from)) peers.get(from).p2pAt = now
	// Per connection, not per name: a reloaded peer keeps its name but counts from 0 again.
	if (via === 'ride') {
		const entry = peers.get(from)
		if (!entry || msg.seq <= (entry.rideSeq ?? 0)) return
		entry.rideSeq = msg.seq
	}
	if (msg.type === 'ping') {
		if (role === 'runner') postVia(route, 'pong', { t0: msg.t, t1: now }, from)
		return
	}
	if (msg.type === 'pong') {
		if (role !== 'remote' || msg.payload?.t0 == null) return
		clocks[route] = addClockSample(clocks[route].samples, clockSample(msg.payload.t0, msg.payload.t1, now))
		if (route === 'relay') lastPongAt = now
		set({ lastMessageAt: now })
		evaluate()
		return
	}
	set({ lastMessageAt: now })
	handlers.get(msg.type)?.forEach(fn => fn(msg.payload, { from, seq: msg.seq, t: msg.t }))
}

/** Ably `link_offer`/`link_answer`/`link_ice` (`name`) addressed to this client. The runner answers offers from connected remotes only. */
export const receiveSignal = (name, { from, to, sdp, candidate } = {}) => {
	if (to !== ABLY_CLIENT_ID || !from) return
	const kind = name.replace('link_', '')
	const { role, runner } = snapshot
	if (kind === 'offer') {
		if (role !== 'runner' || FORCE_RELAY || !remoteNames().includes(from)) return
		closePeer(from)
		openPeer(from, false).peer.signal('offer', sdp)
		return
	}
	if (role === 'runner' ? !remoteNames().includes(from) : role !== 'remote' || from !== runner) return
	peers.get(from)?.peer.signal(kind, kind === 'ice' ? candidate : sdp)
}

let started = false

/** From the first DC presence on (nothing to link before it): re-evaluates only when an input the route depends on changes, never per dispatch. */
export const startDesktopLink = () => {
	if (started) return
	started = true
	store.subscribe(onStore)
	onStore()
}

const onStore = () => {
	const { dc_state, runner_device, dc_connections } = store.getState().app
	if (
		dc_state === inputs.dc_state &&
		runner_device === inputs.runner_device &&
		dc_connections === inputs.dc_connections
	)
		return
	inputs = { dc_state, runner_device, dc_connections }
	evaluate()
}

const desktopLink = { send, on, onChange, getSnapshot, toRunnerTime, fromRunnerTime, receive, receiveSignal }
if (IS_DEV) window.desktopLink = desktopLink
export default desktopLink
